/**
 * `/market/checkout-return` — where a processor sends the buyer back to, and the one
 * screen on this track that exists to be HONEST about not knowing yet.
 *
 *   ?orderId=<uuid>  which order the buyer was paying for.
 *
 * WHY THIS PAGE IS NOT A RECEIPT. A processor's return URL is hit by the BROWSER, and the
 * browser's arrival says nothing about whether the money moved: the authority on that is
 * the webhook, which may land a second before or a second after. A page that said
 * "purchase complete" because the redirect happened would be telling the buyer something
 * it cannot know, and it would be wrong exactly in the cases that matter — a declined
 * card, an abandoned 3DS step, a delivery still in flight.
 *
 * So it says what is true: the order's own status, read from our own row, and a sentence
 * naming what it is waiting for. It polls, because the honest alternative is asking a
 * person to refresh.
 *
 * WHY THE PATH IS `checkout-return` AND NOT `checkout/return`. TanStack's flat file
 * routing reads a dot as a path separator, so `checkout.return.tsx` would make this a
 * CHILD of `/market/checkout` — and that route renders a screen with no `<Outlet/>`, so
 * the child would never appear. A hyphen keeps it a sibling, which is what it is: the
 * two screens are different moments and neither contains the other.
 *
 * The id is kept as a free-form string rather than validated as a uuid here, for the same
 * reason `/market/checkout` does: the gateway answers a malformed id exactly the way it
 * answers an id that names nothing, so a client-side format check would only move the
 * refusal earlier and give the two answers different shapes.
 */
import { createFileRoute } from '@tanstack/react-router';

export interface MarketCheckoutReturnSearch {
  orderId?: string;
}

export function validateMarketCheckoutReturnSearch(
  search: Record<string, unknown>,
): MarketCheckoutReturnSearch {
  const raw = search?.orderId;
  const orderId = typeof raw === 'string' && raw.length > 0 && raw.length <= 64 ? raw : undefined;
  return { ...(orderId ? { orderId } : {}) };
}

export const Route = createFileRoute('/_authenticated/market/checkout-return')({
  validateSearch: validateMarketCheckoutReturnSearch,
});
